import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useAccountAuth } from '../hooks/useAccountAuth';
import { openExternal } from '../lib/openExternal';
import { track } from '../utils/analytics';
import { getPlatform } from '../utils/platformUtils';
import { MESSENGER_GROUP_URL, DISCORD_URL } from '../config/appLinks';
import {
  SUPPORT_EMAIL,
  buildBugReportMailto,
  buildBugReportText,
  buildSupportMailto,
  collectBugReportMeta,
} from '../lib/bugReport';
import {
  YouCard,
  YouRow,
  YouRowGroup,
  SectionLabel,
  SubScreenHeader,
  PrimaryButton,
} from './YouPrimitives';

const TEXT_SECONDARY = 'rgba(255,255,255,0.7)';
const TEXT_MUTED = 'rgba(255,255,255,0.55)';
const TEXT_TERTIARY = 'rgba(255,255,255,0.4)';

export default function HelpSection({
  onBack,
  screen = 'you',
  faqCount = 0,
  knownIssues = [],
  onOpenFaq,
  onOpenKnownIssues,
}) {
  const { user } = useAccountAuth();
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(null);

  useEffect(() => () => clearTimeout(copyTimer.current), []);

  const meta = collectBugReportMeta({ userId: user?.id, screen });

  const handleCopy = async () => {
    track('bug_report_started', { platform: getPlatform(), fallback: true });
    try {
      await navigator.clipboard.writeText(buildBugReportText(meta));
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // Presse-papiers refuse : le mailto reste le chemin principal.
    }
  };

  const openCount = knownIssues.filter((issue) => issue?.status === 'open').length;
  const showAnswers = faqCount > 0 || knownIssues.length > 0;
  const showCommunity = !!MESSENGER_GROUP_URL || !!DISCORD_URL;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <SubScreenHeader title="Help & Support" onBack={onBack} />

      <YouCard>
        <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#fff', mb: '8px' }}>
          Something not working?
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 300, color: TEXT_SECONDARY, mb: '16px' }}>
          Tell us what happened. We attach your app version, device and screen automatically, so you
          don't have to.
        </Typography>

        <PrimaryButton
          component="a"
          href={buildBugReportMailto(meta)}
          onClick={() => track('bug_report_started', { platform: getPlatform(), fallback: false })}
          startIcon={<BugReportOutlinedIcon />}
        >
          Report a bug
        </PrimaryButton>

        <Typography sx={{ textAlign: 'center', fontSize: 13, color: TEXT_MUTED, mt: '12px' }}>
          {'or write to '}
          <Box
            component="a"
            href={buildSupportMailto()}
            sx={{ color: 'var(--accent)', textDecoration: 'none' }}
          >
            {SUPPORT_EMAIL}
          </Box>
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="text"
            onClick={handleCopy}
            sx={{ fontSize: 13, color: TEXT_MUTED, textTransform: 'none' }}
          >
            {copied ? 'Copied' : 'Copy report'}
          </Button>
        </Box>
      </YouCard>

      {showAnswers && (
        <YouRowGroup>
          {faqCount > 0 && (
            <YouRow
              icon={<HelpOutlineIcon />}
              title="FAQ"
              subtitle="Answers to common questions"
              onClick={onOpenFaq}
            />
          )}
          {knownIssues.length > 0 && (
            <YouRow
              icon={<BugReportOutlinedIcon />}
              title="Known issues"
              subtitle="What we're fixing"
              onClick={onOpenKnownIssues}
              trailing={
                openCount > 0 ? (
                  <Box
                    sx={{
                      backgroundColor: '#1c1c1f',
                      borderRadius: '11px',
                      minWidth: 22,
                      padding: '0 7px',
                      fontSize: 12,
                      fontWeight: 500,
                      color: TEXT_SECONDARY,
                      textAlign: 'center',
                    }}
                  >
                    {openCount}
                  </Box>
                ) : null
              }
            />
          )}
        </YouRowGroup>
      )}

      {showCommunity && (
        <>
          <SectionLabel>Community</SectionLabel>
          <YouRowGroup>
            {!!MESSENGER_GROUP_URL && (
              <YouRow
                icon={<ForumOutlinedIcon />}
                title="Per's Mentorship group"
                subtitle="Messenger, for members who joined with Per's code"
                onClick={() => openExternal(MESSENGER_GROUP_URL)}
              />
            )}
            {!!DISCORD_URL && (
              <YouRow
                icon={<ForumOutlinedIcon />}
                title="upScale Discord"
                subtitle="Bugs, ideas, help. Open to everyone"
                onClick={() => openExternal(DISCORD_URL)}
              />
            )}
          </YouRowGroup>
        </>
      )}

      <Typography sx={{ textAlign: 'center', fontSize: 12, color: TEXT_TERTIARY }}>
        We answer within 24 hours
      </Typography>
    </Box>
  );
}
